import { BriefDto, IdeaDto } from "@growth/dto";
import { CONTENT_FORMATS, ContentFormat } from "@growth/schema";
import { Badge, Button, Field, Input, Textarea } from "@growth/ui";
import { LoaderCircle, Search, Settings2, Sparkles, X } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ErrorText, PageHeader } from "../components/page-header";
import { useActive } from "../lib/active";
import { apiErrorMessage } from "../lib/api-error";
import { usePlanIdea } from "../lib/calendar";
import { useContentSettings } from "../lib/content";
import { formatRelative } from "../lib/format";
import {
  useBriefs,
  useIdeaAction,
  useIdeas,
  useResearchSettings,
  useSaveResearchSettings,
  useSetup,
  useStartBrief,
} from "../lib/research";

/** The weekly brief and topic research: what the audience asks, what competitors do, and ideas with sources. */
export const ResearchPage = () => {
  const { brand, atLeast } = useActive();
  const briefs = useBriefs();
  const setup = useSetup();
  const saved = useIdeas("saved");
  const [showSettings, setShowSettings] = useState(false);
  const [latest, ...older] = briefs.data ?? [];

  return (
    <section className="max-w-6xl space-y-6">
      <PageHeader
        eyebrow={brand?.name ?? ""}
        title="Research"
        actions={
          <Button variant="secondary" onClick={() => setShowSettings((open) => !open)}>
            <Settings2 className="size-4" /> Research settings
          </Button>
        }
      >
        Every Monday the researcher writes a brief: what the audience is asking, what competitors
        did, what's coming up, and ideas with the evidence behind them. You can also research a
        topic now.
      </PageHeader>

      {setup.data && !setup.data.done && (
        <p className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm">
          Research works best once the brand profile is confirmed.{" "}
          <Link to="/setup" className="font-medium text-accent underline">
            Finish brand setup
          </Link>
        </p>
      )}
      {showSettings && <SettingsCard onClose={() => setShowSettings(false)} />}
      {atLeast("EDITOR") && <TopicForm />}

      {briefs.isError && <ErrorText>{apiErrorMessage(briefs.error)}</ErrorText>}
      {briefs.isPending && (
        <LoaderCircle className="size-5 animate-spin text-muted" aria-label="Loading" />
      )}
      {briefs.data?.length === 0 && (
        <p className="rounded-xl border border-line bg-white p-6 text-center text-sm text-muted">
          No research yet. The first weekly brief arrives on Monday once setup is done, or research
          a topic now.
        </p>
      )}
      {latest && <Brief brief={latest} open />}

      {saved.data && saved.data.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-muted">
            Saved for later
          </h2>
          <div className="grid gap-3 md:grid-cols-2">
            {saved.data.map((idea) => (
              <IdeaCard key={idea.id} idea={idea} />
            ))}
          </div>
        </div>
      )}

      {older.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-muted">
            Earlier research
          </h2>
          {older.map((brief) => (
            <Brief key={brief.id} brief={brief} />
          ))}
        </div>
      )}
    </section>
  );
};

const TopicForm = () => {
  const start = useStartBrief();
  const [focus, setFocus] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    start.mutate(focus.trim(), { onSuccess: () => setFocus("") });
  };
  return (
    <form
      onSubmit={submit}
      className="flex flex-wrap items-end gap-2 rounded-xl border border-line bg-white p-4"
    >
      <Field label="Research a topic now" className="min-w-64 flex-1">
        {(props) => (
          <Input
            {...props}
            required
            minLength={3}
            maxLength={300}
            placeholder="e.g. video interviews with no one on the other side"
            value={focus}
            onChange={(event) => setFocus(event.target.value)}
          />
        )}
      </Field>
      <Button type="submit" disabled={start.isPending}>
        {start.isPending ? (
          <LoaderCircle className="size-4 animate-spin" />
        ) : (
          <Search className="size-4" />
        )}
        Research
      </Button>
      {start.isError && <ErrorText>{apiErrorMessage(start.error)}</ErrorText>}
    </form>
  );
};

const Brief = ({ brief, open = false }: { brief: BriefDto; open?: boolean }) => {
  const title =
    brief.kind === "weekly" ? `Weekly brief · week of ${brief.weekOf}` : `Topic: ${brief.focus}`;
  const ideas = brief.ideas.filter((idea) => idea.status !== "dismissed");
  const dismissed = brief.ideas.length - ideas.length;
  return (
    <details open={open} className="group rounded-xl border border-line bg-white">
      <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-2 p-4">
        <span className="font-serif text-xl text-ink">{title}</span>
        <span className="flex items-center gap-2 text-xs text-muted">
          {brief.status === "running" && (
            <Badge tone="accent">
              <LoaderCircle className="size-3 animate-spin" /> Researching
            </Badge>
          )}
          {brief.status === "failed" && (
            <Badge className="text-red-700 ring-red-200">Stopped</Badge>
          )}
          {formatRelative(brief.createdAt)} · US${brief.costUsd.toFixed(2)}
        </span>
      </summary>
      <div className="space-y-5 border-t border-line p-4">
        {brief.status === "running" && (
          <p className="text-sm text-muted">
            The researcher is searching. This takes a few minutes.
          </p>
        )}
        {brief.status === "failed" && brief.error && <ErrorText>{brief.error}</ErrorText>}
        {brief.summary && (
          <p className="break-words text-[15px] leading-relaxed text-ink">{brief.summary}</p>
        )}

        {ideas.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-muted">
              Ideas, with the evidence behind them
            </h3>
            <div className="grid gap-3 md:grid-cols-2">
              {ideas.map((idea) => (
                <IdeaCard key={idea.id} idea={idea} />
              ))}
            </div>
            {dismissed > 0 && <p className="text-xs text-muted">{dismissed} turned down.</p>}
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-3">
          <Findings title="What the audience is asking" items={brief.questions} />
          <Findings title="Competitor moves" items={brief.competitorMoves} />
          <Findings
            title="Coming up"
            items={brief.comingUp.map((item) => ({ ...item, sources: [] }))}
          />
        </div>
      </div>
    </details>
  );
};

const Findings = ({ title, items }: { title: string; items: BriefDto["questions"] }) =>
  items.length ? (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold">{title}</h3>
      <ul className="space-y-2 text-sm">
        {items.map((item) => (
          <li key={item.text}>
            {item.text}{" "}
            {item.sources.map((source) => (
              <a
                key={source.url}
                href={source.url}
                target="_blank"
                rel="noreferrer"
                className="mr-1 text-xs text-accent underline"
              >
                {source.title}
              </a>
            ))}
          </li>
        ))}
      </ul>
    </div>
  ) : null;

const IdeaCard = ({ idea }: { idea: IdeaDto }) => {
  const { atLeast } = useActive();
  const navigate = useNavigate();
  const settings = useContentSettings();
  const act = useIdeaAction();
  const [mode, setMode] = useState<"view" | "make" | "dismiss">("view");
  const plan = usePlanIdea();
  // The brand's own formats first; the idea's suggestions are ticked to start with.
  const brandFormats = (settings.data?.channels.flatMap((channel) => channel.formats) ??
    []) as ContentFormat[];
  const options = (
    brandFormats.length ? brandFormats : Object.keys(CONTENT_FORMATS)
  ) as ContentFormat[];
  const suggested = (idea.formats as ContentFormat[]).filter((format) => options.includes(format));
  const [chosen, setChosen] = useState<ContentFormat[]>(
    suggested.length ? suggested : options.slice(0, 1),
  );
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const pillar = settings.data?.pillars.find((item) => item.id === idea.pillar);

  return (
    <article className="flex flex-col gap-2 rounded-lg border border-line p-4">
      <div className="flex items-start justify-between gap-2">
        <h4 className="font-serif text-lg leading-snug text-ink">{idea.title}</h4>
        {idea.status === "used" && <Badge tone="success">Made</Badge>}
        {idea.status === "saved" && <Badge>Saved</Badge>}
      </div>
      {pillar && <p className="text-xs text-muted">Pillar: {pillar.name}</p>}
      <p className="break-words text-sm leading-relaxed text-ink/80">{idea.why}</p>
      {idea.sources.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {idea.sources.map((source) => (
            <a
              key={source.url}
              href={source.url}
              target="_blank"
              rel="noreferrer"
              className="rounded-full bg-paper px-2.5 py-0.5 text-xs text-ink hover:underline"
            >
              {source.title}
            </a>
          ))}
        </div>
      )}
      {idea.formats.length > 0 && (
        <p className="text-xs text-muted">
          Suggested as:{" "}
          {idea.formats.map((item) => CONTENT_FORMATS[item as ContentFormat] ?? item).join(" · ")}
        </p>
      )}
      {idea.pieceIds.length > 0 && (
        <p className="text-xs">
          {idea.pieceIds.map((id, index) => (
            <Link key={id} to={`/content/${id}`} className="mr-2 text-accent underline">
              Piece {index + 1}
            </Link>
          ))}
        </p>
      )}

      {atLeast("EDITOR") && mode === "view" && idea.status !== "dismissed" && (
        <div className="mt-auto flex flex-wrap gap-2 pt-1">
          <Button onClick={() => setMode("make")}>
            <Sparkles className="size-4" /> Make it
          </Button>
          {idea.status === "new" && (
            <Button
              variant="secondary"
              disabled={act.isPending}
              onClick={() => act.mutate({ kind: "save", id: idea.id })}
            >
              Save for later
            </Button>
          )}
          {idea.status !== "used" && (
            <Button variant="ghost" onClick={() => setMode("dismiss")}>
              Not for us…
            </Button>
          )}
        </div>
      )}
      {mode === "make" && (
        <div className="space-y-3 rounded-lg bg-paper/60 p-3">
          <fieldset className="space-y-1">
            <legend className="text-xs font-medium">Make it for</legend>
            <div className="grid gap-1 sm:grid-cols-2">
              {options.map((value) => (
                <label key={value} className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    className="size-3.5"
                    checked={chosen.includes(value)}
                    onChange={(event) =>
                      setChosen(
                        event.target.checked
                          ? [...chosen, value]
                          : chosen.filter((item) => item !== value),
                      )
                    }
                  />
                  {CONTENT_FORMATS[value]}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="flex flex-wrap items-end gap-2">
            <Button
              disabled={act.isPending || plan.isPending || chosen.length === 0 || chosen.length > 6}
              onClick={() =>
                act.mutate(
                  { kind: "make", id: idea.id, formats: chosen },
                  {
                    onSuccess: (result) =>
                      result.pieceIds[0] && navigate(`/content/${result.pieceIds[0]}`),
                  },
                )
              }
            >
              {act.isPending ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : (
                <Sparkles className="size-4" />
              )}
              Make now
            </Button>
            <span className="text-xs text-muted">or plan it for</span>
            <Input
              type="date"
              aria-label="Day to post"
              className="h-9 w-40"
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
            <Button
              variant="secondary"
              disabled={
                !date || plan.isPending || act.isPending || chosen.length === 0 || chosen.length > 6
              }
              onClick={() =>
                plan.mutate(
                  { id: idea.id, date, time: "09:00", formats: chosen },
                  { onSuccess: () => navigate("/calendar") },
                )
              }
            >
              Plan it
            </Button>
            <Button variant="ghost" aria-label="Cancel" onClick={() => setMode("view")}>
              <X className="size-4" />
            </Button>
          </div>
          {chosen.length > 6 && <p className="text-xs text-amber-800">Up to six at a time.</p>}
          {plan.isError && <ErrorText>{apiErrorMessage(plan.error)}</ErrorText>}
        </div>
      )}
      {mode === "dismiss" && (
        <div className="space-y-2">
          <Textarea
            aria-label="Why not"
            rows={2}
            maxLength={500}
            placeholder="Why not? The researcher reads this and stops suggesting that kind of idea."
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <div className="flex gap-2">
            <Button
              variant="secondary"
              disabled={reason.trim().length < 3 || act.isPending}
              onClick={() =>
                act.mutate(
                  { kind: "dismiss", id: idea.id, reason },
                  { onSuccess: () => setMode("view") },
                )
              }
            >
              Turn it down
            </Button>
            <Button variant="ghost" onClick={() => setMode("view")}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {act.isError && <ErrorText>{apiErrorMessage(act.error)}</ErrorText>}
    </article>
  );
};

const SettingsCard = ({ onClose }: { onClose: () => void }) => {
  const { atLeast } = useActive();
  const current = useResearchSettings();
  const save = useSaveResearchSettings();
  const [form, setForm] = useState<{
    weeklyEnabled: boolean;
    weeklyBudgetUsd: string;
    steer: string;
  } | null>(null);
  if (current.isPending) return <LoaderCircle className="size-5 animate-spin text-muted" />;
  if (current.isError) return <ErrorText>{apiErrorMessage(current.error)}</ErrorText>;
  const values = form ?? {
    weeklyEnabled: current.data.settings.weeklyEnabled,
    weeklyBudgetUsd: String(current.data.settings.weeklyBudgetUsd),
    steer: current.data.settings.steer,
  };
  const canEdit = atLeast("OWNER");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(
      {
        weeklyEnabled: values.weeklyEnabled,
        weeklyBudgetUsd: Number(values.weeklyBudgetUsd),
        steer: values.steer,
      },
      { onSuccess: () => setForm(null) },
    );
  };

  return (
    <form onSubmit={submit} className="space-y-4 rounded-xl border border-line bg-white p-5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="font-serif text-xl text-ink">Research settings</h2>
          <p className="text-xs text-muted">
            This week (from {current.data.weekOf}): US${current.data.spentThisWeekUsd.toFixed(2)} of
            US$
            {current.data.settings.weeklyBudgetUsd.toFixed(2)} spent on research.
          </p>
        </div>
        <Button type="button" variant="ghost" aria-label="Close" onClick={onClose}>
          <X className="size-4" />
        </Button>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="size-4"
          disabled={!canEdit}
          checked={values.weeklyEnabled}
          onChange={(event) => setForm({ ...values, weeklyEnabled: event.target.checked })}
        />
        Write a brief every Monday
      </label>
      <Field
        label="Weekly research budget (US$)"
        hint="Setup, briefs, topics and trials together. Research stops when it's used up."
      >
        {(props) => (
          <Input
            {...props}
            type="number"
            min={0}
            max={100}
            step={0.5}
            disabled={!canEdit}
            value={values.weeklyBudgetUsd}
            onChange={(event) => setForm({ ...values, weeklyBudgetUsd: event.target.value })}
            className="max-w-32"
          />
        )}
      </Field>
      <Field label="What the researcher should always know">
        {(props) => (
          <Textarea
            {...props}
            rows={2}
            maxLength={2000}
            disabled={!canEdit}
            value={values.steer}
            onChange={(event) => setForm({ ...values, steer: event.target.value })}
          />
        )}
      </Field>
      {save.isError && <ErrorText>{apiErrorMessage(save.error)}</ErrorText>}
      {canEdit ? (
        <Button type="submit" disabled={!form || save.isPending}>
          Save
        </Button>
      ) : (
        <p className="text-xs text-muted">Only owners change research settings.</p>
      )}
    </form>
  );
};
